import React, { ComponentProps } from 'react'
import { dropdownMenuStyle } from '../../components/simple/dropdown'
import * as RadixPopover from '@radix-ui/react-popover'

interface PopoverProps extends ComponentProps<typeof RadixPopover.Content> {
  /** The trigger is what the popover is attached to. It does not automatically open the popover. If you want that behavior, use the <HoverPopover/> component. */
  trigger: React.ReactNode
  /** The content of the popover. This is shown when the popover is open. */
  children: React.ReactNode
  /** Callback when the popover is opened or closed */
  onOpenChange?: (open: boolean) => void
  /** Whether the popover is open or closed */
  open?: boolean
  /** How the popover is aligned to the trigger */
  align?: 'start' | 'center' | 'end'
  /** Additional class names to add to the background of the popover. If not provided, the default dropdownMenuStyle is used */
  backgroundStyle?: string
  /** How far to popover tries to be from the sides of the screen */
  collisionPadding?: number
  /** If true, the popover will hide by unmounting the portal rather than just closing the radix popover. This makes it close faster, which can avoid flickering if the content is changed at the same time it is hidden */
  aggressivelyHide?: boolean
  /** Whether to show an arrow pointer to the trigger on the edge of the popover */
  arrow?: boolean
}

/**
 * A component that portals content next to a trigger when open.
 * It uses Radix Popover under the hood. This component is controlled by the open prop, and does not automatically open or close.
 */
export const Popover: React.FC<PopoverProps> = ({
  onOpenChange,
  open,
  trigger,
  children,
  className,
  backgroundStyle,
  collisionPadding = 8,
  aggressivelyHide,
  sideOffset = 3.5,
  arrow = true,
  ...rest
}) => {
  return (
    <RadixPopover.Root onOpenChange={onOpenChange} open={open}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      {(open || !aggressivelyHide) && (
        <RadixPopover.Portal>
          <RadixPopover.Content
            {...rest}
            sideOffset={sideOffset}
            collisionPadding={collisionPadding}
            className={`focus:outline-none ${className} ${backgroundStyle || dropdownMenuStyle}`}
          >
            {children}
            {arrow && (
              <RadixPopover.Arrow asChild height={5}>
                <div className="-my-px h-[6px] w-[12px] overflow-hidden">
                  <div className="relative bottom-[19px] left-[-4px] right-[-4px] h-[20px] w-[20px] rotate-45 rounded-br-sm border border-gray-100 bg-white" />
                </div>
              </RadixPopover.Arrow>
            )}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      )}
    </RadixPopover.Root>
  )
}

interface HoverPopoverProps {
  /** The trigger is what the popover is attached to. The popover will automatically open when the trigger is hovered. */
  trigger: React.ReactElement
  /** The content of the popover. This is shown when the popover is open. */
  children: React.ReactNode
  /** How long to wait before closing the popover after the mouse leaves. */
  gracePeriod?: number
  /** Additional class names to add to the background of the popover. If not provided, the default dropdownMenuStyle is used */
  backgroundStyle?: string
}

/**
 * A component that portals content next to a trigger when hovered.
 * It uses Radix Popover under the hood. This component is uncontrolled, and will automatically open and close based on the trigger being hovered.
 */
export const HoverPopover: React.FC<HoverPopoverProps> = ({
  trigger,
  children,
  gracePeriod = 200,
  backgroundStyle,
}) => {
  const [open, setOpen] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(false)
    }, gracePeriod)
  }

  const triggerWithHandlers = React.cloneElement(trigger, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleMouseEnter,
    onBlur: handleMouseLeave,
  })

  return (
    <Popover trigger={triggerWithHandlers} open={open} backgroundStyle={backgroundStyle}>
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
    </Popover>
  )
}
